import { Injectable } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { DPLocation } from '../models/DPLocation';
import { DPValidation } from '../models/DPValidation';

/**
 * Collection of utility functions
 */
@Injectable({
  providedIn: 'root'
})
export class UtilsService {
  public static readonly TaskReasons = [
    '',
    'Expiring Soon',
    'Expired',
    'Missing Document',
    'Missing Information',
    'User Created'
  ];
  public static readonly TaskStatus = [
    '',
    'New',
    'Uploaded - Please allow 2 business days for the upload to be processed',
    'Rejected',
    'Completed',
    'In Progress'
  ];
  public static readonly TaskStatusColors = [
    '',
    '#0acf97',
    '#ffbc00',
    '#fa5c7c',
    '#ffbc00',
    '#ffbc00'
  ];
  public static readonly DPTaskType = [
    '',
    'FormSubmissionFull',
    'FormSubmissionPartial',
    'Upload Front/Back',
    'Upload Multipage'
  ];
  public static readonly ALLOWED_UPLOAD_TYPES = [
    'jpg',
    'jpeg',
    'png',
    'tif',
    'tiff',
    'pdf'
  ];
  public static readonly IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'tif', 'tiff'];
  public static readonly FILESIZE_UNITS = ['B', 'KB', 'MB', 'GB'];

  public getTaskReason(reasonId: number) {
    return UtilsService.TaskReasons[reasonId] || '';
  }

  public getTaskStatus(statusId: number) {
    return UtilsService.TaskStatus[statusId] || '';
  }

  public getTaskStatusColor(statusId: number) {
    return UtilsService.TaskStatusColors[statusId] || '#000';
  }

  public getTaskType(taskTypeId: number) {
    return UtilsService.DPTaskType[taskTypeId] || '';
  }

  /**
   * Get current date in format of `MM-DD-YYYY`
   */
  public getToday() {
    const now = new Date();
    return `${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}`;
  }

  /**
   * Compares `MM-DD-YYYY` strings as dates
   */
  public compareDates(date1: string | null, date2: string | null) {
    const d1 = this.toDate(date1);
    const d2 = this.toDate(date2);

    if (!d1 || !d2) {
      return 0;
    }

    return d1.getTime() - d2.getTime();
  }

  /**
   * Compares NgbDateStruct dates
   */
  public compareDates1(
    date1: NgbDateStruct | null,
    date2: NgbDateStruct | null
  ) {
    if (!date1 || !date2) {
      return 0;
    }

    return (
      (date1.year - date2.year) * 372 +
      (date1.month - date2.month) * 31 +
      (date1.day - date2.day)
    );
  }

  /**
   * Get difference between 2 dates in month.
   * Dates are strings in format of `MM-DD-YYYY`
   */
  public getMonthDiff(date1: string | null, date2: string | null) {
    const d1 = this.toDate(date1);
    const d2 = this.toDate(date2);

    if (!d1 || !d2) {
      return 0;
    }

    let months = 0;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();

    return months <= 0 ? 0 : months;
  }

  /**
   * Get difference between 2 dates in month.
   */
  public getMonthDiff1(d1: Date, d2: Date) {
    let months = 0;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();

    return months;
  }

  /**
   * Convert startEndDate string to object.
   * Valid format for startEndDate is "MM-DD-YYYY to MM-DD-YYYY".
   *
   * @param startEndDate
   * @returns {Object} Objects having start date and end date
   */
  public startEndDateToObject(startEndDate: string) {
    const [startStr, endStr] = startEndDate.split(' to ');
    return {
      start: new Date(startStr),
      end: new Date(endStr)
    };
  }

  /**
   * Checks and sorts start and end date.
   *
   * This method will convert date strings to Date object
   * and sort start/end date in ascending order.
   *
   * @param dates [startDate, endDate]
   * @returns Array of date objects or null
   */
  public normalizeStartEndDate(dates: string[]) {
    const d0 = this.toDate(dates[0]);
    const d1 = this.toDate(dates[1]);

    if (d0 && d1) {
      return d0.getTime() > d1.getTime() ? [d1, d0] : [d0, d1];
    }

    return null;
  }

  public dMax(date1: Date, date2: Date) {
    return date1 > date2 ? date1 : date2;
  }

  public dMin(date1: Date, date2: Date) {
    return date1 < date2 ? date1 : date2;
  }

  public datesIntersection(dates1: Date[], dates2: Date[]) {
    if (!dates1 || !dates2) {
      return null;
    }

    if (dates1[0] <= dates2[1] && dates2[1] <= dates1[1]) {
      return [this.dMax(dates1[0], dates2[0]), dates2[1]];
    } else if (dates1[0] <= dates2[0] && dates2[0] <= dates1[1]) {
      return [dates2[0], this.dMin(dates1[1], dates2[1])];
    } else if (dates1[0] <= dates2[0] && dates1[1] >= dates2[1]) {
      return [dates2[0], dates2[1]];
    } else if (dates1[0] >= dates2[0] && dates1[1] <= dates2[1]) {
      return [dates1[0], dates1[1]];
    }

    return null;
  }

  public datesUnion(dates1: Date[], dates2: Date[]) {
    if (!dates1 || !dates2) {
      return null;
    }

    if (dates1[0] <= dates2[1] && dates2[1] <= dates1[1]) {
      return [this.dMin(dates1[0], dates2[0]), dates1[1]];
    } else if (dates1[0] <= dates2[0] && dates2[0] <= dates1[1]) {
      return [dates1[0], this.dMax(dates1[1], dates2[1])];
    } else if (dates1[0] <= dates2[0] && dates1[1] >= dates2[1]) {
      return [dates1[0], dates1[1]];
    } else if (dates1[0] >= dates2[0] && dates1[1] <= dates2[1]) {
      return [dates2[0], dates2[1]];
    }

    return null;
  }

  /**
   * Add new date segment to dateSegments.
   *
   * @param dateSegments Existing date segments
   * @param newSegment New date segment [start, end]
   * @returns
   */
  public pushDateSegment(dateSegments: Date[][], newSegment: Date[]) {
    if (!newSegment) {
      return;
    }

    let startIndex = 0;
    let startDate: Date | null = null;
    let endIndex = 0;
    let endDate: Date | null = null;

    /**
     * Find all intersecting date segments.
     */
    for (let j = 0; j < dateSegments.length; j++) {
      const segment = dateSegments[j];
      if (segment[0] <= newSegment[0]) {
        startIndex = j;
        startDate = segment[0];
        endIndex = j;
        endDate = segment[1];
      } else if (segment[0] <= newSegment[1]) {
        endIndex = j;
        endDate = segment[1];
      } else {
        j = dateSegments.length;
      }
    }

    if (!startDate) {
      /**
       * If start date is empty, the new segment will come before existing segments.
       */
      const du = this.datesUnion(dateSegments[0], newSegment);
      if (du) {
        dateSegments.splice(0, 1, du);
      } else {
        dateSegments.unshift(newSegment);
      }
    } else {
      /**
       * Replace all intersecting segments with new segment.
       */
      const du = this.datesUnion([startDate, endDate!], newSegment);
      if (du) {
        dateSegments.splice(startIndex, endIndex - startIndex + 1, du);
      } else {
        dateSegments.splice(startIndex + 1, 0, newSegment);
      }
    }
  }

  /**
   * Get all date segments before a date.
   *
   * @param dateSegments
   * @param d
   * @returns New date segments
   */
  public getLimitedDateSegmentsByMaxDate(dateSegments: Date[][], d: Date) {
    const newSegments: Date[][] = [];

    for (let i = 0; i < dateSegments.length; i++) {
      if (dateSegments[i][0] >= d) {
        break;
      } else if (dateSegments[i][1] >= d) {
        newSegments.push([dateSegments[i][0], d]);
        break;
      } else {
        newSegments.push(dateSegments[i]);
      }
    }

    return newSegments;
  }

  /**
   * Get a date segemts after a date
   *
   * @param dateSegments
   * @param d
   * @returns New date segments
   */
  public getLimitedDateSegmentsByMinDate(dateSegments: Date[][], d: Date) {
    const newSegments: Date[][] = [];

    for (let i = dateSegments.length - 1; i >= 0; i--) {
      if (dateSegments[i][1] <= d) {
        break;
      } else if (dateSegments[i][0] <= d) {
        newSegments.push([d, dateSegments[i][1]]);
        break;
      } else {
        newSegments.push(dateSegments[i]);
      }
    }

    return newSegments.reverse();
  }

  /**
   * Converts date object to `MM-DD-YYYY` string
   */
  public getDateString(d: Date) {
    if (d) {
      return `${this.pad2(d.getMonth() + 1)}-${this.pad2(
        d.getDate()
      )}-${d.getFullYear()}`;
    } else {
      return '';
    }
  }

  /**
   * Converts date object to `MM-DD-YYYY` string
   */
  public getDateString1(date: NgbDateStruct) {
    return `${this.pad2(date.month)}-${this.pad2(date.day)}-${this.pad2(
      date.year
    )}`;
  }

  /**
   * Convert `MM-DD-YYYY` string to Date object
   */
  public toDate(ds: string | Date | null) {
    let date: Date | null = null;
    if (typeof ds === 'object') {
      date = new Date(ds);
    } else {
      const [m, d, y] = (ds || '').split('-').map((d) => +d);
      date = new Date(y, m - 1, d);
    }

    return isNaN(date.getTime()) ? null : date;
  }

  public isValidDate(d: string) {
    return !!this.toDate(d);
  }

  public dateToNgbDateStruct(d: Date | null): NgbDateStruct | null {
    return d
      ? {
          year: d.getFullYear(),
          month: d.getMonth() + 1,
          day: d.getDate()
        }
      : null;
  }

  public pad2(n: number): string {
    const i = Math.floor(Math.abs(n));
    return i < 10 ? `0${i}` : i.toString();
  }

  /**
   * Format size
   * @param size Size in bytes
   * @returns Formatted size
   */
  public formatSize(size: number) {
    if (isNaN(size) || size < 0) {
      return '';
    }

    for (let i = 0; i < UtilsService.FILESIZE_UNITS.length; i++) {
      if (size < 1024) {
        return `${size.toFixed(2).replace(/\.?0+$/, '')}${
          UtilsService.FILESIZE_UNITS[i]
        }`;
      }
      size /= 1024;
    }

    return `${size.toFixed(2).replace(/\.?0+$/, '')}TB`;
  }

  /**
   * Get company subdomain from url
   */
  public getSubdomain() {
    const [companyDomain] = location.host.split('.');
    return companyDomain;
  }

  /**
   * Get file extension from filename
   */
  public getExtension(filename: string) {
    const splitted = filename.toLowerCase().split('.');
    return splitted[splitted.length - 1];
  }

  /**
   * Check if file is an image based on file name
   */
  public isImage(filename: string) {
    return UtilsService.IMAGE_TYPES.includes(this.getExtension(filename));
  }

  /**
   * Check if file is allowed type to upload.
   */
  public isAllowedFile(filename: string) {
    return UtilsService.ALLOWED_UPLOAD_TYPES.includes(
      this.getExtension(filename)
    );
  }

  /**
   * Wait until an element with selector is available on DOM
   * and calls callback with the element.
   */
  public waitForElement(selector: string, callback: (el: HTMLElement) => void) {
    let timer = setInterval(() => {
      const element = document.querySelector(selector) as HTMLElement;
      if (element) {
        callback(element);
        clearInterval(timer);
      }
    }, 100);
  }

  /**
   * Wait until callback becomes true and resolves the promise.
   */
  public waitFor(callback: () => boolean, timeout = 5000) {
    const ts = new Date().getTime();

    return new Promise((resolve, reject) => {
      if (callback()) {
        resolve(true);
      }

      const timer = setInterval(() => {
        if (callback()) {
          clearInterval(timer);
          resolve(true);
          return;
        }

        const ts1 = new Date().getTime();
        if (ts1 - ts > timeout) {
          reject('timeout');
        }
      });
    });
  }

  /**
   * Get location item in location tree
   */
  public getLocation(tree: DPLocation, locationId: number) {
    if (tree.locationId === locationId) {
      return tree;
    }

    let result: DPLocation;
    tree.childLocation?.some((subtree) => {
      const location = this.getLocation(subtree, locationId);
      if (location) {
        result = location;
        return true;
      }

      return false;
    });

    return result;
  }

  /**
   * Convert date string to NgbDateStruct
   */
  public toNgbDateStruct(dateStr: string) {
    if (dateStr) {
      const matches1 = dateStr.match(
        /^(?<year>\d{4})\-(?<month>\d{2})-(?<date>\d{2})/
      );
      if (matches1) {
        return {
          day: +matches1.groups['date'],
          month: +matches1.groups['month'],
          year: +matches1.groups['year']
        };
      }

      const matches2 = dateStr.match(
        /^(?<month>\d{2})\-(?<date>\d{2})\-(?<year>\d{4})/
      );
      if (matches2) {
        return {
          day: +matches2.groups['date'],
          month: +matches2.groups['month'],
          year: +matches2.groups['year']
        };
      }
    }

    return null;
  }

  public mapArray(arr: any[], mappingShape: Record<string, string>) {
    return arr.map((item) => {
      const newItem: any = {};

      for (const key in mappingShape) {
        newItem[key] = item[mappingShape[key]];
      }

      return newItem;
    });
  }

  /**
   * Transform specified fields in an object into particular formats.
   * This can be used to convert API response to typed format.
   * This object values should not be multi-dimension array. The array has to
   * be array of objects, dates or other primitive types
   * @param obj Object to convert
   * @param shape Shape object specifying value types
   */
  public transformObject(obj: any, shape: object) {
    for (const key in shape) {
      const value = obj[key];

      if (Array.isArray(value)) {
        value.forEach((item, i) => {
          if (typeof item === 'object') {
            this.transformObject(item, shape[key]);
          } else if (typeof item === 'string') {
            value[i] = this.transformString(item, shape[key]);
          }
        });
      } else if (typeof value === 'object' && value !== null) {
        this.transformObject(value, shape[key]);
      } else if (typeof value === 'string') {
        obj[key] = this.transformString(value, shape[key]);
      }
    }
  }

  /**
   * Transform string value to specified type
   * @param strVal String value
   * @param toType Type to convert. Can be 'number', 'date', 'boolean'
   * @returns Number, Date, Boolean depending on the toType
   */
  private transformString(strVal: string, toType: string) {
    switch (toType) {
      case 'number':
        return +strVal;
      case 'date':
        return new Date(strVal);
      case 'boolean':
        return strVal.toLowerCase().trim() === 'true';
      default:
        return strVal;
    }
  }

  /**
   * Get value of validation rule
   * @param rule string or function
   * @returns value
   */
  public getRuleValue(rule: DPValidation | null) {
    if (typeof rule.value === 'function') {
      return rule.value();
    } else if (rule) {
      return rule.value;
    } else {
      return null;
    }
  }

  public fromYesNoToBoolean(value: string | null) {
    if (value === 'Yes') {
      return true;
    } else if (value === 'No') {
      return false;
    } else {
      return null;
    }
  }

  public fromBooleanToYesNo(value: boolean | null) {
    if (value === true) {
      return 'Yes';
    } else if (value === false) {
      return 'No';
    } else {
      return null;
    }
  }

  public isNil(value: any) {
    return value === undefined || value === null;
  }

  public generateID() {
    let result = '';

    for (let i = 0; i < 10; i++) {
      const n = Math.floor(Math.random() * 26);
      if (n < 10) {
        result += n;
      } else {
        result += String.fromCharCode(97 + n - 10);
      }
    }

    return result;
  }
}
